import { Typography } from "@mui/material";
import { AnnouncementModal } from "components/ProductAnnouncement/AnnouncementModal";
import { ProductAnnouncementPhase } from "components/ProductAnnouncement/useProductAnnouncement";

import informationGuideIcon from "../../../../../assets/AnnouncementIcons/information-guide.png";
import { WASTE_STEPPER_MODAL_COPY } from "./wasteStepperAnnouncementConfig";

type WasteStepperAnnouncementProps = {
  phase: ProductAnnouncementPhase;
  /** False once the user has dismissed this for good; the reopen hint only means something while it can still show on its own. */
  isPending: boolean;
  dismissModal: () => void;
  clickPrimaryCta: () => void;
};

/**
 * One-time announcement that document submissions now open in the guided
 * stepper. Route-scoped to the Submissions grid — the page whose row links
 * changed destination — rather than mounted app-wide like the Scout launch.
 *
 * No "don't show again" checkbox and no soft dismiss — with a single screen
 * and no chained tour, every way of closing it (the X, the backdrop, or "Got
 * it") is terminal, so it shows exactly once no matter how it's closed.
 *
 * Takes its `useProductAnnouncement` result as props, called once by the grid
 * page, so the same surface backs both this modal and the grid's "show me
 * again" help icon.
 */
export function WasteStepperAnnouncement({
  phase,
  isPending,
  dismissModal,
  clickPrimaryCta,
}: WasteStepperAnnouncementProps) {
  return (
    <AnnouncementModal
      open={phase === "modal"}
      onDismiss={dismissModal}
      reopenHintText={isPending ? WASTE_STEPPER_MODAL_COPY.reopenHint : undefined}
      titleId="waste-stepper-announcement-title"
      dismissAriaLabel="Dismiss submissions announcement"
      hero={
        <img
          src={informationGuideIcon}
          alt=""
          style={{ width: 56, height: 56, objectFit: "contain" }}
        />
      }
      title={WASTE_STEPPER_MODAL_COPY.headline}
      subtitle={WASTE_STEPPER_MODAL_COPY.subheadline}
      primaryCtaLabel={WASTE_STEPPER_MODAL_COPY.primaryLabel}
      onPrimaryCta={clickPrimaryCta}
    >
      <Typography variant="body1" color="text.primary">
        Each submission now opens as a short series of steps instead of one long form. You can see
        why the filing is required, what Encamp is preparing, and where it stands.{" "}
        <strong>Nothing about what Encamp files for you has changed.</strong>
      </Typography>
    </AnnouncementModal>
  );
}
